// src/features/authz/ModelPanel.tsx
// Authorization model info: browse version history, view a model's type
// definitions (raw JSON), and publish a new model version from DSL.
// See docs/ux/authz-ia.md section 5.

import { useEffect, useState } from 'react';
import { useI18n, useAuthz } from '@react/hooks';
import { Button, Textarea, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import type { AuthorizationModel } from '@core/types/authz';

function PublishModelForm({ onPublished }: { onPublished: () => void }) {
  const { t } = useI18n();
  const store = useAuthz();
  const [schema, setSchema] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schema.trim()) return;
    setIsSaving(true);
    try {
      const r = await store.WriteAuthorizationModel({ schema: schema.trim() });
      setResultId(r.authorization_model_id);
      onPublished();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">{t('authz.model.publishTitle')}</h3>
      <Textarea
        label={t('authz.model.dsl')}
        value={schema}
        onChange={(e) => setSchema(e.target.value)}
        rows={8}
        className="font-mono text-xs"
        placeholder={'model\n  schema 1.1\n\ntype user\n\ntype document\n  relations\n    define viewer: [user]'}
      />
      <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : t('authz.model.publish')}</Button>
      {resultId && <p className="text-sm text-success">{t('authz.model.published', { id: resultId })}</p>}
    </form>
  );
}

export function ModelPanel() {
  const { t } = useI18n();
  const store = useAuthz();
  const [versions, setVersions] = useState<AuthorizationModel[]>([]);
  const [selected, setSelected] = useState<AuthorizationModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingModel, setIsLoadingModel] = useState(false);
  const [showPublish, setShowPublish] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const r = await store.ListAuthorizationModels();
      setVersions(r.authorization_models?.items ?? []);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = async (id: string) => {
    setIsLoadingModel(true);
    try {
      const r = await store.ReadAuthorizationModel(id);
      setSelected(r.authorization_model ?? null);
    } finally {
      setIsLoadingModel(false);
    }
  };

  return (
    <div className="space-y-4">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{t('authz.model.versionsTitle')}</h3>
        <Button variant="ghost" size="sm" onClick={() => setShowPublish((v) => !v)}>{t('authz.model.publishTitle')}</Button>
      </div>

      {showPublish && <PublishModelForm onPublished={() => { setShowPublish(false); load(); }} />}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('authz.model.col.id')}</TableHead>
            <TableHead>{t('authz.model.col.schemaVersion')}</TableHead>
            <TableHead>{t('authz.model.col.types')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
          ) : versions.length === 0 ? (
            <TableEmpty colSpan={4} message={t('authz.model.noVersions')} />
          ) : (
            versions.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="font-mono text-xs">{m.id}</TableCell>
                <TableCell>{m.schema_version}</TableCell>
                <TableCell>{m.type_definitions.length}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleView(m.id)} disabled={isLoadingModel}>{t('common.view')}</Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {selected && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{t('authz.model.detailFor', { id: selected.id })}</p>
          <pre className="max-h-96 overflow-auto rounded-md bg-muted/40 p-3 text-xs">{JSON.stringify(selected, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
